import { useState, useEffect, useRef, useCallback } from 'react';
import { MSG_LIFETIME } from '../lib/energy';
import { colorFromName } from '../lib/colors';

export interface PulseUser {
  clientId: string;
  name: string;
  color: string;
  buffer: string;
  lastActivity: number | null;
}

export interface PulseMessage {
  id: string;
  clientId: string;
  text: string;
  submittedAt: number;
}

interface PulseState {
  connected: boolean;
  myId: string | null;
  myName: string;
  myColor: string;
  roomName: string;
  users: Map<string, PulseUser>;
  messages: PulseMessage[];
  sendBuffer: (text: string) => void;
  sendSubmit: () => void;
  sendName: (name: string) => void;
  sendColor: (color: string) => void;
  sendRoomName: (name: string) => void;
}

function getUid(): string {
  let uid = localStorage.getItem('pulse-uid');
  if (!uid) {
    uid = crypto.randomUUID();
    localStorage.setItem('pulse-uid', uid);
  }
  return uid;
}

export function usePulseSocket(spaceId: string): PulseState {
  const [connected, setConnected] = useState(false);
  const [myId, setMyId] = useState<string | null>(null);
  const [myName, setMyName] = useState('');
  const [myColor, setMyColor] = useState('#888');
  const [roomName, setRoomName] = useState('');
  const [users, setUsers] = useState<Map<string, PulseUser>>(new Map());
  const [messages, setMessages] = useState<PulseMessage[]>([]);

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const uidRef = useRef(getUid());

  // Clean up expired messages periodically
  useEffect(() => {
    const iv = setInterval(() => {
      const now = Date.now();
      setMessages(prev => prev.filter(m => now - m.submittedAt < MSG_LIFETIME));
    }, 2000);
    return () => clearInterval(iv);
  }, []);

  const send = useCallback((msg: unknown) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const connect = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws/pulse/${spaceId}?uid=${uidRef.current}`);
    socketRef.current = ws;

    ws.addEventListener('open', () => {
      setConnected(true);
      reconnectRef.current = 0;
    });

    ws.addEventListener('message', (event) => {
      try {
        const msg = JSON.parse(event.data);

        switch (msg.type) {
          case 'sync': {
            setMyId(msg.clientId);
            setMyColor(msg.color);
            if (msg.roomName) setRoomName(msg.roomName);
            setUsers(new Map(
              (msg.users as PulseUser[]).map(u => [u.clientId, { ...u, lastActivity: null }])
            ));
            // Restore saved preferences from localStorage
            const savedName = localStorage.getItem('pulse-name');
            const savedColor = localStorage.getItem('pulse-color');
            if (savedName) {
              setMyName(savedName);
              setMyColor(savedColor || colorFromName(savedName));
              ws.send(JSON.stringify({ type: 'setName', name: savedName }));
              if (savedColor) {
                ws.send(JSON.stringify({ type: 'setColor', color: savedColor }));
              }
            } else {
              setMyName(msg.name);
              if (savedColor) {
                setMyColor(savedColor);
                ws.send(JSON.stringify({ type: 'setColor', color: savedColor }));
              }
            }
            break;
          }

          case 'join':
            setUsers(prev => {
              const next = new Map(prev);
              next.set(msg.clientId, {
                clientId: msg.clientId,
                name: msg.name,
                color: msg.color,
                buffer: '',
                lastActivity: Date.now(),
              });
              return next;
            });
            break;

          case 'leave':
            setUsers(prev => {
              const next = new Map(prev);
              next.delete(msg.clientId);
              return next;
            });
            break;

          case 'buffer':
            setUsers(prev => {
              const existing = prev.get(msg.clientId);
              if (!existing) return prev;
              const next = new Map(prev);
              next.set(msg.clientId, {
                ...existing,
                buffer: msg.text,
                lastActivity: Date.now(),
              });
              return next;
            });
            break;

          case 'submit':
            setMessages(prev => [
              ...prev,
              {
                id: msg.msgId,
                clientId: msg.clientId,
                text: msg.text,
                submittedAt: Date.now(),
              },
            ]);
            // Clear the user's buffer
            setUsers(prev => {
              const existing = prev.get(msg.clientId);
              if (!existing) return prev;
              const next = new Map(prev);
              next.set(msg.clientId, {
                ...existing,
                buffer: '',
                lastActivity: Date.now(),
              });
              return next;
            });
            break;

          case 'nameChange':
            setUsers(prev => {
              const existing = prev.get(msg.clientId);
              if (!existing) return prev;
              const next = new Map(prev);
              next.set(msg.clientId, {
                ...existing,
                name: msg.name,
                ...(msg.color ? { color: msg.color } : {}),
              });
              return next;
            });
            break;

          case 'roomName':
            setRoomName(msg.name ?? '');
            break;

          case 'colorChange':
            setUsers(prev => {
              const existing = prev.get(msg.clientId);
              if (!existing) return prev;
              const next = new Map(prev);
              next.set(msg.clientId, { ...existing, color: msg.color });
              return next;
            });
            break;

          case 'ping':
            try { ws.send(JSON.stringify({ type: 'pong' })); } catch {}
            break;
        }
      } catch {
        // Ignore malformed messages
      }
    });

    ws.addEventListener('close', () => {
      setConnected(false);
      socketRef.current = null;

      // Reconnect with exponential backoff
      if (reconnectRef.current < 10) {
        const delay = Math.min(1000 * Math.pow(2, reconnectRef.current), 30000);
        reconnectRef.current++;
        reconnectTimerRef.current = setTimeout(connect, delay);
      }
    });

    ws.addEventListener('error', () => {
      ws.close();
    });
  }, [spaceId]);

  useEffect(() => {
    connect();

    const cleanup = () => socketRef.current?.close();
    window.addEventListener('pagehide', cleanup);
    window.addEventListener('beforeunload', cleanup);

    return () => {
      window.removeEventListener('pagehide', cleanup);
      window.removeEventListener('beforeunload', cleanup);
      clearTimeout(reconnectTimerRef.current);
      socketRef.current?.close();
    };
  }, [connect]);

  const sendBuffer = useCallback((text: string) => send({ type: 'buffer', text }), [send]);
  const sendSubmit = useCallback(() => send({ type: 'submit' }), [send]);
  const sendName = useCallback((name: string) => {
    setMyName(name);
    send({ type: 'setName', name });
  }, [send]);
  const sendColor = useCallback((color: string) => {
    setMyColor(color);
    send({ type: 'setColor', color });
  }, [send]);
  const sendRoomName = useCallback((name: string) => {
    setRoomName(name);
    send({ type: 'setRoomName', name });
  }, [send]);

  return { connected, myId, myName, myColor, roomName, users, messages, sendBuffer, sendSubmit, sendName, sendColor, sendRoomName };
}
